/**
 * ReplyComposerStrip — "replying to Stephen · Here's the meeting link…", in
 * the band directly above the composer (reply to a specific message, Miles
 * 2026-09-15). Same band, same margins as DictationStrip and the attachment
 * previews: the composer's own antechamber.
 *
 * Three things, in the order they matter: WHO is being answered, WHAT they
 * said (one line, ellipsised), and a cancel target big enough for a thumb.
 * The text is a button that jumps to the original — the same gesture the
 * quoted block in a bubble offers. `role="status"` so a screen-reader user
 * hears that the next message will be a reply.
 *
 * Gold is a fill, never text on a light ground: accent by border colour, the
 * name in --brand-gold-text.
 */
import React from 'react';
import styled from 'styled-components';
import { QUOTED_REPLY_COPY } from './QuotedReply';

const StripRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 12px;
  margin: 0 0 6px;
  border: 1px solid var(--brand-line);
  border-left: 3px solid var(--brand-gold);
  border-radius: 12px;
  background: var(--brand-canvas);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  color: var(--brand-text-secondary);
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const JumpText = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 2px 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

const Who = styled.span`
  font-weight: 600;
  font-size: 0.72rem;
  color: var(--brand-gold-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Words = styled.span`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CancelButton = styled.button`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  background: #f5f5f5;
  color: #444;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: #eee;
    color: #222;
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  /* Touch: the one control here somebody reaches for one-handed. */
  @media (hover: none) {
    width: 44px;
    height: 44px;
  }
`;

// copy: reply_strip_chrome
export const REPLY_STRIP_COPY = {
  en: { cancel: 'cancel reply' },
  es: { cancel: 'cancelar respuesta' },
} as const;

export interface ReplyComposerStripProps {
  label: string;
  excerpt?: string;
  onCancel: () => void;
  onJump?: () => void;
  lang?: 'en' | 'es';
}

export function ReplyComposerStrip({ label, excerpt, onCancel, onJump, lang = 'en' }: ReplyComposerStripProps): React.ReactElement {
  const quoted = QUOTED_REPLY_COPY[lang] ?? QUOTED_REPLY_COPY.en;
  const copy = REPLY_STRIP_COPY[lang] ?? REPLY_STRIP_COPY.en;
  const who = (label || '').trim();
  const words = (excerpt || '').trim();
  return (
    <StripRow role="status" aria-live="polite" data-reply-composer-strip="">
      <JumpText type="button" title={quoted.jump} aria-label={`${quoted.replyingTo} ${who} — ${quoted.jump}`} onClick={() => onJump?.()}>
        <Who data-reply-strip-who="">{`${quoted.replyingTo} ${who}`}</Who>
        <Words data-reply-strip-excerpt="">{words || quoted.earlier}</Words>
      </JumpText>
      <CancelButton type="button" data-reply-strip-cancel="" aria-label={copy.cancel} title={copy.cancel} onClick={onCancel}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </CancelButton>
    </StripRow>
  );
}

export default ReplyComposerStrip;
